<script setup>
import ga4 from '#common/utils/ga4'
import giftCardImage from '~/assets/images/giftcard-2021.png'
import { reset } from '@formkit/core'

definePageMeta({
  middleware: (to, from) => {
    // Reset gift card form when navigating to the main gift card page,
    // unless coming from the payment page (to allow for editing the order).
    if (from.path !== '/giftcard/payment/') {
      const giftcardCheckout = useGiftcardCheckout()
      giftcardCheckout.resetData()
      reset('giftcard-form')
    } else {
      // Let the component know not to reset the form state.
      to.meta.fromPayment = true
    }
  },
})

const VALUE_OPTIONS = [25, 50, 75, 100, 150, 200, 250]
const { $auth } = useNuxtApp()
const giftcardCheckout = useGiftcardCheckout()
const $route = useRoute()
const $router = useRouter()
const agreement = ref(null)
const form = ref(null)
const metadata = useMetadata()
const formData = computed({
  get: () => giftcardCheckout.formData,
  set: (val) => giftcardCheckout.setFormData(val),
})

const errorMessage = computed(() => {
  if ($route.query.error === 'incomplete') {
    return 'Please complete all required fields before proceeding to payment.'
  }
  return ''
})
const valueOptions = computed(() =>
  VALUE_OPTIONS.map((val) => ({ value: val, label: `$${val}` })),
)
const unavailableStates = computed(() => {
  const nonDtcStates = metadata.settingsVars.COMPLIANCE_STATE_GROUPS.non_dtc
  const unavailableStates = []
  nonDtcStates.forEach((x) => {
    if (metadata.stateCodesToNames[x]) {
      unavailableStates.push(metadata.stateCodesToNames[x])
    }
  })

  if (unavailableStates.length === 0) {
    return false
  }

  if (unavailableStates.length <= 2) {
    return unavailableStates.join(' and ')
  }

  return (
    unavailableStates.slice(0, unavailableStates.length - 1).join(', ') +
    ', and ' +
    unavailableStates.slice(-1)
  )
})
onMounted(() => {
  ga4.track('view_gift_cards')
  agreement.value = null
})

onActivated(() => {
  ga4.track('view_gift_cards')
  agreement.value = null
  // Only reset the form's validation state if we are not returning from payment.
  if (!$route.meta.fromPayment) {
    giftcardCheckout.resetData()
    reset('giftcard-form')
  }
})

function submit(data) {
  // Only proceed if the user has agreed to the terms
  if (agreement.value) {
    // Find all keys in the form data that match the pattern for recipients (e.g., recipients.abc123)
    const recipientKeys = Object.keys(data).filter((k) =>
      /^recipients\.[a-z0-9-]+$/.test(k),
    )
    // Transform each recipient entry into an object with its id and data
    const recipients = recipientKeys.map((key) => {
      const id = key.split('.')[1]
      return {
        ...data[key], // Spread the recipient's form data
        id, // Add the id as a property
      }
    })
    // Create a new object without the recipient keys
    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([k]) => !recipientKeys.includes(k)),
    )
    // Create a normalized data object with recipients as an array
    const normalizedData = {
      ...filteredData,
      recipients,
    }
    // Save the normalized form data to the store
    giftcardCheckout.setFormData(normalizedData)
    // Navigate to the payment page
    $router.push({ path: '/giftcard/payment/' })
  } else {
    // If agreement is not checked, set it to false to trigger validation error
    agreement.value = false
  }
}

function addRecipient() {
  giftcardCheckout.addRecipient()
}

function removeRecipient(id) {
  giftcardCheckout.removeRecipient(id)
}
</script>

<template>
  <main>
    <div class="giftcards-landing">
      <header class="section-header background-maroon">
        <h1>Give the Gift of World-Class Wine</h1>
      </header>
      <section class="giftcards-intro">
        <div class="row">
          <div class="columns large-6 photo">
            <img
              :src="giftCardImage"
              alt="Wine Access Gift Card"
              role="presentation"
            />
          </div>
          <div class="columns large-6 text">
            <h2>
              Looking for a gift they definitely won’t return?<br />
              Give them access to the world’s best wines.
            </h2>
            <p>
              Wine Access e-Gift Cards can be redeemed online for any of our
              expertly-curated selections. Select the amount and let the
              recipient pick the bottle, whether it’s a Michelin-star-level
              staple from Bordeaux or a boutique discovery from the Willamette
              Valley.
            </p>
            <p class="disclaimer">
              Gift cards are delivered by email and contain instructions for
              redemption. Gift card purchases cannot be combined with wine
              purchases.
              <span v-if="unavailableStates"
                >Not available in {{ unavailableStates }}.
              </span>
            </p>
          </div>
        </div>
      </section>
      <section class="giftcards-form">
        <div class="form-container">
          <FormKit
            id="giftcard-form"
            ref="form"
            v-model="formData"
            type="form"
            :actions="false"
            @submit="submit"
          >
            <div class="striped-form-rows">
              <div
                v-for="recipient in formData.recipients"
                :key="recipient.id"
                class="row-item"
              >
                <FormKit type="group" :name="`recipients.${recipient.id}`">
                  <div class="row relative" aria-live="polite">
                    <div class="columns large-3">
                      <FormKit
                        name="receiver_name"
                        validation="required"
                        label="Recipient Name"
                        :validation-messages="{
                          required: 'Error: Name is required.',
                        }"
                      />
                    </div>
                    <div class="columns large-4">
                      <FormKit
                        type="email"
                        name="receiver_email"
                        validation="bail|required|email"
                        label="Recipient Email"
                        :validation-messages="{
                          required: 'Error: Email is required.',
                          email: 'Error: Please enter a valid email address.',
                        }"
                      />
                    </div>
                    <div class="columns small-6 large-2">
                      <FormKit
                        type="select"
                        :options="valueOptions"
                        label="Value"
                        name="value"
                        validation="required"
                        placeholder="Select..."
                        :validation-messages="{
                          required: 'Error: Required',
                        }"
                      />
                    </div>
                    <div class="columns small-6 large-1 remove-column end">
                      <button
                        v-if="formData.recipients.length > 1"
                        type="button"
                        class="remove"
                        :style="{ 'padding-top': '20px' }"
                        @click="removeRecipient(recipient.id)"
                      >
                        Remove
                        <span class="large-up-hide">this recipient</span>
                      </button>
                    </div>
                  </div>
                </FormKit>
              </div>
              <div class="add">
                <button type="button" @click="addRecipient">
                  Add another recipient
                </button>
              </div>
            </div>

            <div class="row">
              <div class="columns large-6">
                <DateSelectFK name="send_date" label="Send Date" />
              </div>
              <div class="columns large-6">
                <ErrorsAfterLabelInputFK
                  type="text"
                  validation="required"
                  label="Sender Name"
                  name="sender_name"
                />
              </div>
            </div>

            <div v-if="!$auth.loggedIn" class="row">
              <div class="columns large-12">
                <ErrorsAfterLabelInputFK
                  type="email"
                  validation="bail|required|email"
                  label="Sender Email"
                  name="sender_email"
                  :validation-messages="{
                    email: 'Error: Please enter a valid email address.',
                  }"
                />
              </div>
            </div>

            <div class="row">
              <div class="columns">
                <div class="textarea-with-counter">
                  <ErrorsAfterLabelInputFK
                    type="textarea"
                    name="receiver_message"
                    label="Gift Note"
                    validation="bail|required|length:0,250"
                  >
                    <template #label="{ label, id, visibleValidationErrors }"
                      ><label v-if="formData.recipients.length > 1" :for="id"
                        >{{ label }} (<strong>Important:</strong> All recipients
                        will receive the same message.)<span
                          v-if="visibleValidationErrors?.length"
                          class="error"
                          >• Error: {{ visibleValidationErrors[0] }}</span
                        ></label
                      ></template
                    >
                  </ErrorsAfterLabelInputFK>
                  <span class="counter"
                    >{{ (formData.receiver_message || '').length }}/250</span
                  >
                </div>
              </div>
            </div>

            <div class="row">
              <div class="columns medium-12">
                <div class="checkmark-form-input">
                  <input
                    id="giftcards-terms"
                    v-model="agreement"
                    type="checkbox"
                  />
                  <label for="giftcards-terms"
                    >I agree to the Wine Access gift card
                    <a href="/giftcard/terms/" target="_blank">
                      terms &amp; conditions
                    </a>
                  </label>
                  <span v-if="agreement === false" class="error"
                    >Error: Agreement is required</span
                  >
                  <span v-if="errorMessage" class="error">
                    {{ errorMessage }}
                  </span>
                </div>
              </div>
            </div>

            <div class="row giftcards-bottom">
              <div class="columns medium-12">
                <FormKit type="submit" class="buy" label="Buy Now" />

                <p class="giftcards-disclaimer">
                  This Wine Access Gift Card is issued by Wine Access Card
                  Fulfillment, LLC and may only be used up to its remaining
                  balance to purchase merchandise sold by Wine Access and its
                  affiliates on
                  <a href="http://www.wineaccess.com">www.wineaccess.com</a> or
                  its affiliated websites. Not a credit or debit card. Not
                  reloadable. Not redeemable for cash except as required by law.
                  Gift Cards cannot be used to purchase Gift Subscriptions or
                  other Gift Cards. After three years of inactivity, Gift Cards
                  will incur a service fee of $1 per month. These fees will be
                  deducted from the unused balance or value of the Gift Card
                  until the remaining balance or value is redeemed for a
                  purchase or the balance reaches zero, at which time the Gift
                  Card will be deactivated and of no value. Purchase,
                  acceptance, or use of this Wine Access Gift Card constitutes
                  acceptance of the Wine Access Gift Card Terms and Conditions,
                  available at
                  <a href="http://www.wineaccess.com">www.wineaccess.com</a>.
                  Questions or balance inquiries, call
                  <CustomerServicePhoneLink />
                </p>
              </div>
            </div>
          </FormKit>
        </div>
      </section>
    </div>
  </main>
</template>
